/**
 * User Management Service
 * Handles all user-related API calls
 */

import apiService from './api.service';
import { API_ENDPOINTS } from '../config/api.config';
import { UserManagement } from '../types';
import {
  PaginatedResponse,
  UserListParams,
  RegisterRequest,
  UsersListResponse,
  AdminResetPasswordRequest,
  UpdateUserRequest,
} from '../types/api.types';

class UserService {
  /**
   * Get all users with pagination and filters
   */
  async getUsers(params?: UserListParams): Promise<PaginatedResponse<UserManagement>> {
    const response = await apiService.get<UsersListResponse>(
      API_ENDPOINTS.USER.LIST,
      { params }
    );
    
    // Transform the response to match our expected format
    return {
      data: response.data.users,
      total: response.data.pagination.total,
      page: response.data.pagination.page,
      limit: response.data.pagination.limit,
      totalPages: response.data.pagination.totalPages
    };
  }

  /**
   * Get user by ID
   */
  async getUserById(id: string): Promise<UserManagement> {
    return await apiService.get<UserManagement>(API_ENDPOINTS.USER.GET_BY_ID(id));
  }

  /**
   * Create user (using register API)
   */
  async createUser(data: RegisterRequest): Promise<UserManagement> {
    return await apiService.post<UserManagement>(API_ENDPOINTS.USER.CREATE, data);
  }

  /**
   * Update user
   */
  async updateUser(id: string, data: UpdateUserRequest): Promise<UserManagement> {
    return await apiService.put<UserManagement>(
      API_ENDPOINTS.USER.UPDATE(id),
      data
    );
  }

  /**
   * Delete user
   */
  async deleteUser(id: string): Promise<void> {
    await apiService.delete(API_ENDPOINTS.USER.DELETE(id));
  }

  /**
   * Activate user (admin only)
   */
  async activateUser(id: string): Promise<UserManagement> {
    return await apiService.patch<UserManagement>(API_ENDPOINTS.USER.ACTIVATE(id));
  }

  /**
   * Deactivate user (admin only)
   */
  async deactivateUser(id: string): Promise<UserManagement> {
    return await apiService.patch<UserManagement>(API_ENDPOINTS.USER.DEACTIVATE(id));
  }

  /**
   * Admin override: set a new password for any user
   */
  async adminResetPassword(id: string, data: AdminResetPasswordRequest): Promise<void> {
    await apiService.post(API_ENDPOINTS.USER.ADMIN_RESET_PASSWORD(id), data);
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new UserService();
